import React, { useCallback } from 'react';
import { motion, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import styles from './SwipeableContent.module.scss';

interface SwipeableContentProps {
  children: React.ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  currentIndex?: number;
  totalItems?: number;
  disabled?: boolean;
}

const SWIPE_THRESHOLD = 50; // Minimum distance to trigger swipe
const DRAG_ELASTIC = 0.3; // How much resistance when dragging past bounds

export const SwipeableContent: React.FC<SwipeableContentProps> = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  currentIndex = 0,
  totalItems = 1,
  disabled = false
}) => {
  const x = useMotionValue(0);
  
  // Calculate opacity for edge indicators based on drag position
  const leftIndicatorOpacity = useTransform(x, [0, 100], [0, 0.6]);
  const rightIndicatorOpacity = useTransform(x, [-100, 0], [0.6, 0]);

  const handleDragEnd = useCallback((_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    
    // Determine if swipe should trigger based on offset and velocity
    const shouldSwipe = Math.abs(offset) > SWIPE_THRESHOLD || Math.abs(velocity) > 500;
    
    if (shouldSwipe) {
      if (offset > 0 && onSwipeRight) {
        // Swiped right - go to previous
        onSwipeRight();
      } else if (offset < 0 && onSwipeLeft) {
        // Swiped left - go to next
        onSwipeLeft();
      }
    }
  }, [onSwipeLeft, onSwipeRight]);

  // Determine drag constraints based on whether there's a next/prev item
  const canSwipeRight = currentIndex > 0 && onSwipeRight;
  const canSwipeLeft = currentIndex < totalItems - 1 && onSwipeLeft;

  return (
    <div className={styles.container}>
      {/* Left edge indicator (swipe right to go back) */}
      <motion.div 
        className={`${styles.edgeIndicator} ${styles.left}`}
        style={{ opacity: canSwipeRight ? leftIndicatorOpacity : 0 }}
      >
        <span className={styles.indicatorIcon}>‹</span>
      </motion.div>

      {/* Right edge indicator (swipe left to go forward) */}
      <motion.div 
        className={`${styles.edgeIndicator} ${styles.right}`}
        style={{ opacity: canSwipeLeft ? rightIndicatorOpacity : 0 }}
      >
        <span className={styles.indicatorIcon}>›</span>
      </motion.div>

      <motion.div
        className={styles.draggable}
        drag={disabled ? false : 'x'}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={DRAG_ELASTIC}
        onDragEnd={handleDragEnd}
        style={{ x }}
        whileDrag={{ cursor: 'grabbing' }}
      >
        {children}
      </motion.div>
      {/* Dot indicators moved to GameDashboard to avoid overflow:hidden clipping */}
    </div>
  );
};
